import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  AppLayout,
  Box,
  Button,
  ContentLayout,
  Header,
  KeyValuePairs,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
} from "@amzn/awsui-components-react";

import SideNav from "../../Home/SideNav";
import {
  BYOC_ROUTE,
  DEPLOY_STATUS_DISPLAY,
  PAYLOAD_FAMILY_LABEL,
  formatTimestamp,
  getStatusDisplay,
  isOpenable,
} from "./constants";
import { getSolution, listUploads } from "./api";


/**
 * How far a publish got, from the flags codeSubmit writes as it goes.
 *
 * `data_copied` and `cloudfront_invalidated` land as each step completes, so a
 * failed upload can say *where* it failed. "Copied but not invalidated" needs
 * a different fix from "never copied" — the first is serving stale content and
 * wants an invalidation retry, the second never served and wants a re-upload —
 * and a single status cannot tell them apart.
 */
/**
 * Did this entry come from a BI data refresh rather than a code upload?
 *
 * Both land on the same transaction log on purpose — the question the log
 * answers is "what changed this app, and who changed it", and a refresh that
 * replaced the app's data is as much an answer as an upload. They need
 * different wording throughout, though, so this is checked rather than
 * inferred from the fields.
 */
export const isDataRefresh = (upload) => upload?.transaction_type === "DATA_REFRESH";

export const describeProgress = (upload) => {
  if (!upload) return "—";
  if (isDataRefresh(upload)) {
    // A refresh has already finished by the time its row is written, so the
    // publish wording ("not started", "nothing was published") would be
    // wrong — the only thing that can be half-done is the cache.
    return upload.cloudfront_invalidated
      ? "Data replaced and cache cleared"
      : "Data replaced, cache not cleared";
  }
  if (upload.deploy_status === "done") return "Published and cache cleared";
  if (upload.cloudfront_invalidated) return "Cache cleared";
  if (upload.data_copied) return "Files copied, cache not cleared";
  if (upload.deploy_status === "failed") return "Nothing was published";
  return "Not started";
};

/** `s3://bucket/key` → `key`, for a readable table cell. */
export const shortArchivePath = (path) => {
  if (!path) return null;
  const withoutScheme = path.startsWith("s3://") ? path.slice(5) : path;
  const slash = withoutScheme.indexOf("/");
  return slash === -1 ? withoutScheme : withoutScheme.slice(slash + 1);
};

/**
 * The upload's own file id — what the archive in S3 is keyed by.
 *
 * Falls back to the transaction id because they are the same value for every
 * row written so far (`record_transaction_item` sets both from one uuid). It
 * is still read as its own field rather than aliased, so the two can diverge
 * later without this page silently showing the wrong one.
 */
export const fileIdOf = (upload) => upload?.file_id || upload?.transaction_id || null;

/** What altered the app, in the words of whichever kind of entry this is. */
export const describeKind = (upload) => {
  if (!upload) return "—";
  if (isDataRefresh(upload)) return "Data refresh";
  return upload.is_update ? "Update" : "Onboarding";
};

/**
 * The "App" tab — the live solution's link and state.
 *
 * **Apps open on their own address, not in a frame here.** That was settled by
 * measurement and then by design.
 *
 * Measurement: framing the app's own hostname gives
 * `ERR_TOO_MANY_REDIRECTS`. It is a different origin from the portal, so with
 * no session yet on that host the Midway interceptor bounces the frame to
 * midway-auth.amazon.com — a genuinely cross-site hop where a `Lax` cookie is
 * withheld — which returns, finds nothing, and loops. Its unauthenticated
 * response also carries `X-Frame-Options: DENY`.
 *
 * Design: the obvious fix, serving the apps from the PORTAL's origin, is worse
 * rather than better. These are citizen-UPLOADED apps, and `allow-scripts`
 * with `allow-same-origin` on same-origin content lets a framed document drop
 * its own sandbox — so the uploaded code could read the portal DOM and take
 * the Cognito session out of storage. A separate origin is not an obstacle
 * here; it is the isolation boundary.
 *
 * In-page rendering would need a dedicated sandbox origin with
 * `frame-ancestors` for the portal and its own short-lived authorisation —
 * a separate piece of work, not a detail of this page.
 */
export const AppTab = ({ solution }) => {
  const openable = isOpenable(solution);
  const status = getStatusDisplay(solution);
  const link = solution?.link || null;

  return (
    <SpaceBetween size="m">
      {!openable && (
        <Alert type="info" header="Not serving yet">
          {link
            ? `This solution is ${status.label}. The link may not load until it is Active.`
            : "There is no link yet. It will appear here once a deploy succeeds."}
        </Alert>
      )}

      <KeyValuePairs
        columns={2}
        items={[
          {
            label: "Link",
            value: link ? (
              <Link href={link} external>
                {link}
              </Link>
            ) : (
              "— not serving"
            ),
          },
          {
            label: "Status",
            value: <StatusIndicator type={status.type}>{status.label}</StatusIndicator>,
          },
        ]}
      />

      <Box>
        <SpaceBetween size="xs">
          <Button
            variant="primary"
            iconName="external"
            iconAlign="right"
            disabled={!link}
            href={link || undefined}
            target="_blank"
            data-testid="byoc-open-app"
          >
            Open in a new tab
          </Button>
          <Box variant="small" color="text-body-secondary" data-testid="byoc-no-embed-reason">
            Apps open on their own address rather than inside this page. That is
            deliberate: an app is code someone else uploaded, and a separate
            origin is what keeps it away from your session here.
          </Box>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

/**
 * Upload history for one solution — its own page, reached from the BYOC list.
 *
 * Every upload is retained as its own archive and its own transaction row, so
 * this is a real record of what was submitted rather than a reconstruction.
 * That is the reason the archive is keyed by upload id rather than kept in one
 * slot per app: a re-upload never overwrites the evidence of the last one.
 *
 * The solution is loaded here rather than passed in, so the page survives a
 * direct link or a refresh — a history URL someone pasted into a ticket has to
 * work on its own.
 */
const UploadHistoryPage = () => {
  const { codeRefId } = useParams();
  const routerHistory = useHistory();

  const [solution, setSolution] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navOpen, setNavOpen] = useState(true);
  // Opens on the App tab: someone following a link from the list wants the
  // app itself; the history is what they come back for.
  const [activeTab, setActiveTab] = useState("app");

  const load = useCallback(async () => {
    if (!codeRefId) return;
    setLoading(true);
    setError(null);
    try {
      const [found, rows] = await Promise.all([
        getSolution(codeRefId),
        listUploads(codeRefId),
      ]);
      setSolution(found);
      setUploads(rows);
    } catch (caught) {
      setError(
        caught && caught.message ? caught.message : "Could not load the upload history."
      );
    } finally {
      setLoading(false);
    }
  }, [codeRefId]);

  useEffect(() => {
    load();
  }, [load]);

  const columnDefinitions = [
    {
      id: "when",
      header: "When",
      cell: (upload) => formatTimestamp(upload.uploaded_timestamp),
    },
    {
      id: "fileId",
      header: "File id",
      cell: (upload) => (
        // Shown in full rather than truncated: its whole purpose is to be
        // copied into a log search or an S3 path, and a shortened id cannot be.
        <Box variant="small" fontSize="body-s">
          {fileIdOf(upload) || "—"}
        </Box>
      ),
    },
    { id: "who", header: "By", cell: (upload) => upload.uploaded_by || "—" },
    {
      id: "kind",
      header: "Kind",
      cell: (upload) => describeKind(upload),
    },
    { id: "type", header: "Detected", cell: (upload) => upload.byoc_type || "—" },
    {
      id: "status",
      header: "Outcome",
      cell: (upload) => {
        const display = DEPLOY_STATUS_DISPLAY[upload.deploy_status] || {
          label: upload.deploy_status || "—",
          type: "pending",
        };
        return <StatusIndicator type={display.type}>{display.label}</StatusIndicator>;
      },
    },
    {
      id: "progress",
      header: "Progress",
      cell: (upload) => <Box variant="small">{describeProgress(upload)}</Box>,
    },
    {
      id: "files",
      header: "Files",
      cell: (upload) => (upload.objects_written == null ? "—" : String(upload.objects_written)),
    },
    {
      id: "archive",
      header: "Archive",
      cell: (upload) => (
        <Box variant="small">{shortArchivePath(upload.archive_s3_path) || "—"}</Box>
      ),
    },
  ];

  const latest = uploads[0];

  /**
   * Which row the detail panel describes.
   *
   * Defaults to the newest entry, because that is the one being asked about
   * nine times out of ten, and a panel that starts empty would make the page
   * look like it had less to say than it does. Selecting any row replaces it,
   * so the history is actually inspectable rather than only its head.
   *
   * Held as an id, not an object: `load()` replaces every item on refresh, so
   * a stored object would become a stale copy of a row that has since changed.
   */
  const [selectedId, setSelectedId] = useState(null);
  const selected =
    uploads.find((upload) => upload.transaction_id === selectedId) || latest;
  // An id that no longer matches anything — a row that aged out of the page, or
  // a solution switch — falls back to the newest rather than showing nothing.
  const selectedItems = selected ? [selected] : [];

  const historyTab = (
    <SpaceBetween size="l">
          <Table
            items={uploads}
            columnDefinitions={columnDefinitions}
            loading={loading}
            loadingText="Loading upload history"
            trackBy="transaction_id"
            variant="container"
            selectionType="single"
            selectedItems={selectedItems}
            onSelectionChange={({ detail }) =>
              setSelectedId(detail.selectedItems?.[0]?.transaction_id ?? null)
            }
            ariaLabels={{
              selectionGroupLabel: "Upload selection",
              itemSelectionLabel: (data, upload) =>
                `Details for the upload of ${formatTimestamp(upload.uploaded_timestamp)}`,
            }}
            header={
              <Header description="Select a row to see its full detail below.">Uploads</Header>
            }
            data-testid="byoc-history-table"
            empty={
              <Box textAlign="center" padding={{ vertical: "l" }}>
                <SpaceBetween size="xs">
                  <Box variant="strong">No uploads recorded.</Box>
                  <Box variant="small" color="text-body-secondary">
                    Uploads made before history was kept will not appear here.
                  </Box>
                </SpaceBetween>
              </Box>
            }
          />

          {selected && (
            <Box>
              <Box variant="h3" padding={{ bottom: "xs" }}>
                {describeKind(selected)} — {formatTimestamp(selected.uploaded_timestamp)}
                {selected.transaction_id === latest?.transaction_id ? " (latest)" : ""}
              </Box>
              <KeyValuePairs
                columns={2}
                items={[
                  { label: "File id", value: fileIdOf(selected) || "—" },
                  { label: "Transaction id", value: selected.transaction_id },
                  { label: "By", value: selected.uploaded_by || "—" },
                  { label: "Outcome", value: describeProgress(selected) },
                  // A data refresh has no archive and no classification — it
                  // changed the app's data, not its code — so those rows are
                  // replaced by what it actually did rather than shown as dashes.
                  ...(isDataRefresh(selected)
                    ? [
                        {
                          label: "Destination",
                          value: selected.destination_prefix || "app root",
                        },
                        { label: "Source", value: selected.source || "—" },
                        { label: "Objects copied", value: selected.objects_copied ?? "—" },
                        {
                          label: "Objects deleted",
                          value: selected.replaced_all
                            ? `${selected.objects_deleted ?? 0} (replaced all)`
                            : "0 (added and overwrote)",
                        },
                      ]
                    : [
                        {
                          label: "Archive",
                          value: shortArchivePath(selected.archive_s3_path) || "—",
                        },
                        {
                          label: "Classified as",
                          value: selected.classification_reason
                            ? `${selected.byoc_type} — ${selected.classification_reason}`
                            : selected.byoc_type || "—",
                        },
                        { label: "Files written", value: selected.objects_written ?? "—" },
                        { label: "Link", value: selected.deploy_url || "—" },
                      ]),
                  { label: "Invalidation", value: selected.invalidation_id || "—" },
                  ...(selected.error ? [{ label: "Error", value: selected.error }] : []),
                ]}
              />
            </Box>
          )}
    </SpaceBetween>
  );

  const content = (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="The solution itself, and every upload against it."
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={load} disabled={loading} iconName="refresh">
                Refresh
              </Button>
              <Button onClick={() => routerHistory.push(BYOC_ROUTE)}>Back to BYOC</Button>
            </SpaceBetween>
          }
        >
          {solution ? solution.name : "Solution"}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header="Could not load the history" action={<Button onClick={load}>Retry</Button>}>
            {error}
          </Alert>
        )}

        {/* Absent is different from failed: a direct link to a retired or
            mistyped id is a legitimate miss, not an error. */}
        {!loading && !error && !solution && (
          <Alert type="warning" header="No such solution">
            Nothing is registered under id {codeRefId}. It may have been renamed or never
            existed.
          </Alert>
        )}

        {solution && (
          <KeyValuePairs
            columns={4}
            items={[
              { label: "Name", value: solution.name },
              { label: "Id", value: solution.id },
              {
                label: "Type",
                value: PAYLOAD_FAMILY_LABEL[solution.family] || solution.byocType || "—",
              },
              {
                label: "Status",
                value: (() => {
                  const display = getStatusDisplay(solution);
                  return <StatusIndicator type={display.type}>{display.label}</StatusIndicator>;
                })(),
              },
              {
                label: "Link",
                value: solution.link ? (
                  <Link href={solution.link} external>
                    {solution.link}
                  </Link>
                ) : (
                  "— not serving"
                ),
              },
              { label: "Owner", value: solution.owner || "—" },
              { label: "Uploads", value: String(uploads.length) },
              {
                label: "Last upload",
                value: latest ? formatTimestamp(latest.uploaded_timestamp) : "never",
              },
            ]}
          />
        )}

        {solution && (
          <Tabs
            activeTabId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            data-testid="byoc-detail-tabs"
            tabs={[
              {
                id: "app",
                label: "App",
                content: <AppTab solution={solution} />,
              },
              {
                id: "history",
                label: uploads.length ? `Upload history (${uploads.length})` : "Upload history",
                content: historyTab,
              },
            ]}
          />
        )}
      </SpaceBetween>
    </ContentLayout>
  );

  return (
    <AppLayout
      content={content}
      contentType="default"
      toolsHide
      navigationOpen={navOpen}
      onNavigationChange={() => setNavOpen(!navOpen)}
      navigation={<SideNav activeHref={BYOC_ROUTE} />}
    />
  );
};

export default UploadHistoryPage;
